import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { loadTossPayments } from '@tosspayments/payment-sdk';
import { getAdminPublicInfoAPI } from '../../api/charge';
import { colors } from '../../lib/colors';
import { Body2Regular, Body3Regular, Head4, CaptionRegular } from '../../lib/font';
import { Col, Row } from '../../lib/utils';
import ColorRoundButton from '../../components/Button/ColorRoundButton';
import Loading from '../../components/Loading';

type AdminPublicInfo = {
    id: string;
    adminId: string;
    name: string;
    centerName: string;
    profileImageUrl: string;
};

const InputPaymentPage = () => {
    const { adminId } = useParams<{ adminId: string }>();
    const isRappo = adminId === 'rappo';
    const [adminInfo, setAdminInfo] = useState<AdminPublicInfo | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [managerName, setManagerName] = useState('');
    const [centerName, setCenterName] = useState('');

    useEffect(() => {
        const fetchAdminInfo = async () => {
            if (!adminId) {
                setIsError(true);
                setErrorMessage('잘못된 접근입니다.');
                setIsLoading(false);
                return;
            }
            try {
                const res = await getAdminPublicInfoAPI(adminId);
                if (res.status === 200 && res.data?.success) {
                    setAdminInfo(res.data.admin);
                } else if (res.status === 404) {
                    setIsError(true);
                    setErrorMessage('존재하지 않는 계정입니다.');
                } else {
                    setIsError(true);
                    setErrorMessage('정보를 불러오는 데 실패했습니다.');
                }
            } catch {
                setIsError(true);
                setErrorMessage('서버에 연결할 수 없습니다.');
            }
            setIsLoading(false);
        };
        fetchAdminInfo();
    }, [adminId]);

    const isRappoInputValid = !isRappo || (managerName.trim() !== '' && centerName.trim() !== '');

    const handleCardRegister = async () => {
        if (!adminInfo?.id || !adminId) return;
        if (isRappo && !isRappoInputValid) return;

        const clientKey =
            process.env.NODE_ENV === 'development'
                ? process.env.REACT_APP_TOSS_TEST_CLIENT_KEY
                : process.env.REACT_APP_TOSS_LIVE_CLIENT_KEY;

        const origin = window.location.origin;
        let successUrl = `${origin}/input-payment/${adminId}/success`;
        if (isRappo && managerName && centerName) {
            successUrl += `?managerName=${encodeURIComponent(managerName)}&centerName=${encodeURIComponent(centerName)}`;
        }
        const failUrl = `${origin}/input-payment/${adminId}/fail`;

        try {
            const tossPayments = await loadTossPayments(clientKey as string);
            tossPayments
                .requestBillingAuth('카드', {
                    customerKey: adminInfo.id,
                    successUrl,
                    failUrl,
                })
                .catch((error) => {
                    if (error.code !== 'USER_CANCEL') {
                        console.error('토스 결제 오류:', error.code);
                    }
                });
        } catch (error) {
            console.error('토스 페이먼츠 로드 실패:', error);
        }
    };

    if (isLoading) {
        return <Loading text="정보를 불러오는 중입니다..." />;
    }

    if (isError) {
        return (
            <Col style={{ width: '100%', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 16 }}>
                <Col
                    style={{
                        width: '100%',
                        maxWidth: 480,
                        borderRadius: 16,
                        backgroundColor: colors.WHITE_50,
                        border: '1px solid #FFCDD2',
                        padding: '32px 24px',
                        alignItems: 'center',
                    }}
                >
                    <Head4 style={{ color: colors.ERROR }}>오류</Head4>
                    <Body2Regular style={{ color: colors.LAVEL_3, marginTop: 12, textAlign: 'center' }}>
                        {errorMessage}
                    </Body2Regular>
                </Col>
            </Col>
        );
    }

    return (
        <Col
            style={{
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                backgroundColor: '#F5F7FA',
                padding: 16,
            }}
        >
            <Col
                style={{
                    width: '100%',
                    maxWidth: 520,
                    borderRadius: 20,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
                    padding: '32px 20px',
                    alignItems: 'center',
                }}
            >
                {/* 로고 / 타이틀 영역 */}
                <Head4 style={{ color: colors.LAVEL_4, fontSize: 22 }}>Rappo 카드 등록</Head4>
                <Body3Regular style={{ color: colors.LAVEL_2, marginTop: 8, textAlign: 'center' }}>
                    서비스 이용을 위한 결제 카드를 등록해주세요.
                </Body3Regular>

                {/* 센터 정보 */}
                <Col
                    style={{
                        width: '100%',
                        marginTop: 28,
                        padding: '18px 20px',
                        borderRadius: 12,
                        backgroundColor: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                    }}
                >
                    {isRappo ? (
                        <>
                            <Col style={{ marginBottom: 12 }}>
                                <CaptionRegular style={{ color: colors.LAVEL_2, marginBottom: 6 }}>센터명</CaptionRegular>
                                <input
                                    type="text"
                                    value={centerName}
                                    onChange={(e) => setCenterName(e.target.value)}
                                    placeholder="센터명을 입력해주세요"
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        borderRadius: 8,
                                        border: '1px solid #E2E8F0',
                                        fontSize: 14,
                                        outline: 'none',
                                    }}
                                />
                            </Col>
                            <Col>
                                <CaptionRegular style={{ color: colors.LAVEL_2, marginBottom: 6 }}>관리자명</CaptionRegular>
                                <input
                                    type="text"
                                    value={managerName}
                                    onChange={(e) => setManagerName(e.target.value)}
                                    placeholder="관리자명을 입력해주세요"
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        borderRadius: 8,
                                        border: '1px solid #E2E8F0',
                                        fontSize: 14,
                                        outline: 'none',
                                    }}
                                />
                            </Col>
                        </>
                    ) : (
                        <>
                            <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                <CaptionRegular style={{ color: colors.LAVEL_2 }}>센터명</CaptionRegular>
                                <Body3Regular style={{ color: colors.LAVEL_4, fontWeight: 600 }}>
                                    {adminInfo?.centerName || '-'}
                                </Body3Regular>
                            </Row>
                            <Row style={{ justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                                <CaptionRegular style={{ color: colors.LAVEL_2 }}>관리자</CaptionRegular>
                                <Body3Regular style={{ color: colors.LAVEL_4, fontWeight: 600 }}>
                                    {adminInfo?.name || '-'}
                                </Body3Regular>
                            </Row>
                        </>
                    )}
                </Col>

                {/* 안내 문구 */}
                <Col
                    style={{
                        width: '100%',
                        marginTop: 24,
                        padding: '16px 20px',
                        borderRadius: 12,
                        backgroundColor: '#EFF6FF',
                        border: '1px solid #BFDBFE',
                    }}
                >
                    <Body3Regular style={{ color: '#1E40AF', lineHeight: '22px', fontWeight: 600, marginBottom: 8 }}>
                        카드 등록 안내
                    </Body3Regular>
                    <CaptionRegular style={{ color: '#3B82F6', lineHeight: '20px' }}>
                        • 아래 버튼을 클릭하면 토스페이먼츠 카드 등록 화면이 열립니다.
                    </CaptionRegular>
                    <CaptionRegular style={{ color: '#3B82F6', lineHeight: '20px', marginTop: 4 }}>
                        • "개인형 법인카드"의 경우 "개인"으로 등록해주세요.
                    </CaptionRegular>
                    <CaptionRegular style={{ color: '#3B82F6', lineHeight: '20px', marginTop: 4 }}>
                        • 등록된 카드로 매월 정기 결제가 자동으로 진행됩니다.
                    </CaptionRegular>
                </Col>

                {/* 등록 버튼 */}
                <ColorRoundButton
                    text="카드 정보 등록하기"
                    size="middle"
                    callBack={handleCardRegister}
                    customStyle={{ width: '100%', height: 52, marginTop: 32, borderRadius: 12 }}
                    isGradient={true}
                    disableHoverEffect={true}
                    isDisabled={isRappo && !isRappoInputValid}
                />

                <CaptionRegular style={{ color: colors.LAVEL_1, marginTop: 16, textAlign: 'center', lineHeight: '18px' }}>
                    문의사항이 있으시면 카카오 채널 문의하기를 클릭해주세요.
                </CaptionRegular>
                <a
                    href="http://pf.kakao.com/_ICxhcb/chat"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        marginTop: 8,
                        color: colors.primary,
                        fontSize: 14,
                        textDecoration: 'underline',
                    }}
                >
                    카카오 채널 문의하기
                </a>
            </Col>
        </Col>
    );
};

export default InputPaymentPage;
